/**
 * Recognize the short-lived headless report-render entry point (RPT PDF).
 *
 * The subscription email's PDF is this page, rendered by the platform
 * Gotenberg/Chromium renderer at send time. The signed credential stays in
 * the URL fragment, which browsers never send in HTTP requests or access
 * logs; the print app exchanges it through the dedicated request header
 * after boot. Mirrors `runOfShowServerRender.ts`.
 */

export interface ReportServerRenderRequest {
  reportType: string;
  token: string;
}

/**
 * Frontend print routes per report type — the client half of the backend's
 * fail-closed `REPORT_TYPE_FRONTEND_ROUTE` map. A route absent here boots
 * the normal app (which would bounce a headless renderer to /login), so add
 * entries only alongside a working print view for that report.
 */
const REPORT_PRINT_ROUTES: Record<string, string> = {
  '/reports/missing-travel-data': 'missing_travel_data',
  '/reports/missing-staff-info': 'directory_audit',
};

/** `null` when this is not a signed report print visit — a plain app boot. */
export function getReportServerRenderRequest(
  location: Pick<URL, 'pathname' | 'searchParams' | 'hash'>,
): ReportServerRenderRequest | null {
  if (location.searchParams.get('print') !== '1') {
    return null;
  }

  const reportType = REPORT_PRINT_ROUTES[location.pathname];
  if (!reportType) {
    return null;
  }

  const fragment = new URLSearchParams(location.hash.replace(/^#/, ''));
  const token = fragment.get('reportRenderToken');
  if (!token) {
    return null;
  }

  return { reportType, token };
}
